import React, { useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import DashboardTotalCard from "../components/dashboardTotalCard.component";
import apiRequest from "../services/apiClient";
import { useAsyncState } from "../hooks/useAsyncState";

const Dashboard = ({ setTitleProps }) => {
  const { data: dashboardTotals, loading, error, execute } = useAsyncState();
  const hasExecutedRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    setTitleProps("Dashboard");
  }, [setTitleProps]);

  useEffect(() => {
    if (hasExecutedRef.current) return;
    hasExecutedRef.current = true;
    execute(loadDashboardData);
  }, [execute]);

  const loadDashboardData = async () => {
    const response = await apiRequest('dashboard');
    return response.data?.totals;
  };

  const handleRetry = () => {
    hasExecutedRef.current = false;
    execute(loadDashboardData);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((item) => (
            <div key={item} className="animate-pulse">
              <div className="bg-gray-200 rounded-lg h-32 sm:h-36"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
          <div className="flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            Error loading dashboard
          </h3>
          <p className="text-sm text-red-600 mb-4">
            {error.message || 'An unexpected error occurred'}
          </p>
          <button 
            onClick={handleRetry}
            className="btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardTotals) {
    return (
      <div className="text-center py-8">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 max-w-md mx-auto">
          <div className="flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-600">
            No data available
          </h3>
        </div>
      </div>
    );
  }

  const getColorClasses = (color) => {
    switch (color) {
      case 'primary':
        return {
          bg: 'bg-blue-500',
          text: 'text-blue-600',
          border: 'border-blue-200',
          hover: 'hover:border-blue-300'
        };
      case 'secondary':
        return {
          bg: 'bg-gray-500',
          text: 'text-gray-600',
          border: 'border-gray-200',
          hover: 'hover:border-gray-300'
        };
      case 'success':
        return {
          bg: 'bg-green-500',
          text: 'text-green-600',
          border: 'border-green-200',
          hover: 'hover:border-green-300'
        };
      default:
        return {
          bg: 'bg-blue-500',
          text: 'text-blue-600',
          border: 'border-blue-200',
          hover: 'hover:border-blue-300'
        };
    }
  };

  const getIconComponent = (iconName) => {
    switch (iconName) {
      case 'Key':
        return (
          <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        );
      case 'Person':
        return (
          <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case 'FilterNone':
        return (
          <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        );
      default:
        return (
          <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
    }
  };

  const cards = [
    {
      title: "Total Secrets",
      total: dashboardTotals?.secretsCount || 0,
      icon: "Key",
      route: "secrets",
      color: "primary"
    },
    {
      title: "Total Clients", 
      total: dashboardTotals?.clientsCount || 0,
      icon: "Person",
      route: "clients",
      color: "secondary"
    },
    {
      title: "Total Sections",
      total: dashboardTotals?.sectionsCount || 0,
      icon: "FilterNone", 
      route: "sections",
      color: "success"
    }
  ];

  return (
    <div className="p-4">
      {/* Single layout - horizontal cards for all screen sizes */}
      <div className="space-y-3">
        {cards.map((card, index) => (
          <div 
            key={index} 
            onClick={() => navigate(`/${card.route}`)}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer hover:shadow-md hover:border-gray-300 transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-12 h-12 rounded-full ${getColorClasses(card.color).bg} text-white flex items-center justify-center mr-4`}>
                  <div className="w-6 h-6">
                    {getIconComponent(card.icon)}
                  </div>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-800">{card.title}</h3>
                  <div className={`text-xl font-bold ${getColorClasses(card.color).text}`}>
                    {card.total || 0} items
                  </div>
                </div>
              </div>
              <div className="text-gray-400 group-hover:text-gray-600 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;